import { useRef } from 'react';
import { useBoolean, useCountDown, useLocalStorageState, useMount } from 'ahooks';
import { ErrorCode } from '@/constants/error';
import { Flow } from '@/constants/flow';
import { useBizState } from '@/hooks/use-biz-state';
import { useCore } from '@/hooks/use-core';
import { useCoreRequest } from '@/hooks/use-core-request';
import { useEnablePasskeyFlow } from '@/hooks/use-enable-passkey-flow';
import { useFlow } from '@/hooks/use-flow';
import { useLidForm } from '@/hooks/use-lid-form';

const INTERVAL_TIME = 60000;

export function useAuthCode(type: CodeType) {
  const { prevFlow } = useFlow();
  const { userName, areaCode, setBizState, mfa, autoLoginState, supportPassKey } = useBizState();
  const { setFlow, lenovoId, t, lang } = useCore();
  const { control, setError, handleSubmit } = useLidForm({ defaultValues: { authcode: '' } });
  const { doEnablePasskeyFlow } = useEnablePasskeyFlow();
  const bid = window.bid;
  const handleOnError = (errorCode: ErrorCode) => {
    switch (errorCode) {
      case ErrorCode.InvalidVerificationCode:
        setError('authcode', t('invalid_code_error'));
        return true;

      default:
        break;
    }
  };

  const mfaCallback = {
    request: (authcode: string) =>
      lenovoId.loginMfaVerify({
        userName: mfa?.userName || '',
        verifyCode: authcode,
        areaCode: mfa?.areaCode || '',
        bid,
        loginUserName: mfa?.loginUserName || '',
        loginAreaCode: mfa?.loginAreaCode || '',
        autoLoginState: autoLoginState ? '1' : '0',
        authType: mfa?.mfaType?.toString(),
        supportPassKey,
      }),
    onSuccess: ({ data, nextSteps }: CoreRes) => {
      const { authType, areaCode, ...rest } = data;
      setBizState({
        ...rest,
        areaCode,
        authType: areaCode ? 'phone' : 'email',
        oauthGrantAuthType: authType,
      });

      if (nextSteps === Flow.LOGIN_STEP_SUCCESS_CALLBACK && data.callBack) {
        window.location.href = data.callBack;
        return;
      }
      if (nextSteps === Flow.LOGIN_STEP_ENABLE_PASSKEY) {
        doEnablePasskeyFlow(data);
        return;
      }
      if (nextSteps === Flow.LOGIN_STEP_AUTHORIZATION) {
        setFlow(Flow.LOGIN_STEP_AUTHORIZATION);
        return;
      }

      setFlow(Flow.EXCEPTION_SOMETHING_WENT_WRONG);
    },
    onError: (errorCode: ErrorCode) => {
      switch (errorCode) {
        case ErrorCode.InvalidVerificationCode:
          setError('authcode', t('invalid_code_error'));
          return true;
        case ErrorCode.UserDisabled:
          setFlow(Flow.LOGIN_STEP_DISABLE);
          return true;

        default:
          break;
      }
    },
    resendCode: () =>
      lenovoId.loginMfaResend({
        lang,
        userName: mfa?.userName || '',
        areaCode: mfa?.areaCode || '',
        bid,
        tempCode: mfa?.tempCode || '',
      }),
  };

  const mapper = {
    register: {
      request: (authcode: string) => lenovoId.registerVerifyCode({ userName, verifyCode: authcode, areaCode, bid }),
      onSuccess: () => setFlow(Flow.REGISTER_STEP_INPUT_PASSWORD),
      onError: handleOnError,
      resendCode: () => lenovoId.registerResend({ lang, userName, areaCode, bid }),
    },
    reset: {
      request: (authcode: string) =>
        lenovoId.resetPasswordVerifyCode({ userName, verifyCode: authcode, areaCode, bid }),
      onSuccess: () => setFlow(Flow.RESET_PASSWORD_STEP_INPUT_PASSWORD),
      onError: handleOnError,
      resendCode: () => lenovoId.resetPasswordResend({ lang, userName, areaCode, bid }),
    },
    recovery: {
      request: (authcode: string) => lenovoId.recoveryVerifyCode({ userName, verifyCode: authcode, areaCode, bid }),
      onSuccess: () => setFlow(Flow.RECOVERY_STEP_INPUT_PASSWORD),
      onError: handleOnError,
      resendCode: () => lenovoId.recoveryResend({ lang, userName, areaCode, bid }),
    },
    mfa: mfaCallback,
    'enforced-mfa': mfaCallback,
  };

  const { request, onSuccess, resendCode, onError } = mapper[type];
  const { loading, run } = useCoreRequest(
    ({ authcode }: { authcode: string }) => {
      setBizState({ authcode });
      return request(authcode);
    },
    {
      manual: true,
      onSuccess,
      onError,
    },
  );

  // ============== resend logic
  const { run: runResend, loading: resendLoading } = useCoreRequest(resendCode, { manual: true });

  const [open, { setTrue: openTooltip, setFalse: closeTooltip }] = useBoolean(false);
  const [expireState, setExpireState] = useLocalStorageState<Record<string, number>>(`${type}-expire`);
  const targetDate = expireState?.[userName];
  const [c] = useCountDown({ targetDate });
  const countdown = Math.round(c / 1000);
  const tooltipOpen = open && countdown > 0;
  const countdownRef = useRef(countdown);

  const startCountDown = () => {
    setExpireState({ ...expireState, [userName]: Date.now() + INTERVAL_TIME });
  };

  const resend = () => {
    countdownRef.current = countdown;
    //  still in countdown, don't need to resend
    if (countdown !== 0) {
      openTooltip();
      return false;
    }
    startCountDown();
    runResend();
  };

  useMount(() => {
    // If the previous flow was EXCEPTION_REQUEST_BLOCKED, do not resend the verification code
    if (Date.now() < targetDate || prevFlow === Flow.EXCEPTION_REQUEST_BLOCKED) return;
    // If coming from EXCEPTION_EXPIRED_CODE, resend the verification code
    if (prevFlow === Flow.EXCEPTION_EXPIRED_CODE) resend();
    else startCountDown(); // For default steps, the SDK automatically sends the verification code, so start the countdown
  });

  // ============== resend logic end

  return {
    control,
    loading,
    tooltipOpen,
    countdown,
    countdownRef,
    closeTooltip,
    openTooltip,
    submit: handleSubmit(run),
    resend,
    resendLoading,
  };
}
